import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "src/store";

import { updateUniversalLicense } from "@dashboard/devices/store";
import { License } from "@dashboard/devices/types";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { getNestedGroupsWithIndentation } from "@dashboard/groups/utils";
import { useCurrentSubscriptionExpired } from "@dashboard/workspaces/api/helpers";
import { Typography } from "@mui/material";
import { DangerOutlinedButton } from "@shared/CustomButton";
import { SelectField } from "@shared/form/SelectField/SelectField";
import { SingleSelectFieldProps } from "@shared/form/SelectField/SelectField.types";
import { MinimalTableCell } from "@shared/table/MinimalTableComponents";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { Tooltip } from "@shared/Tooltip";

type ActivationCodeCellProps = {
    license: License;
};
export const ActivationCodeCell = (props: ActivationCodeCellProps) => (
    <MinimalTableCell align="left">
        <Typography>{props.license.activationCode}</Typography>
    </MinimalTableCell>
);

type TargetGroupCellProps = {
    license: License;
};
export const TargetGroupCell = (props: TargetGroupCellProps) => {
    const { t } = useTranslation(["pages"]);
    const dispatch = useDispatch();
    const groups = useSelector(selectGroups);
    const isSubscriptionExpired = useCurrentSubscriptionExpired();

    const groupOptions = getNestedGroupsWithIndentation(groups);

    const handleGroupChange: SingleSelectFieldProps["onChange"] = async (_event, selectedOption) => {
        if (!selectedOption || typeof selectedOption.value !== "number") {
            return;
        }

        const groupId = selectedOption.value;
        if (groupId === props.license.universalGroupId) {
            return;
        }

        try {
            await dispatch(updateUniversalLicense({ id: props.license.id, groupId })).unwrap();
            showSuccessToast(t("pages:universalLicensePage.toast.targetGroupChangedSuccessfully"));
        } catch (err) {
            const error = err as Error;
            showErrorToast(error.message || t("pages:universalLicensePage.toast.targetGroupChangeFailed"));
        }
    };

    return (
        <MinimalTableCell align="left">
            <Tooltip title={t("pages:universalLicensePage.tooltip.subscriptionExpired")} hide={!isSubscriptionExpired}>
                <SelectField
                    value={groupOptions.find((option) => option.value === props.license.universalGroupId)}
                    onChange={handleGroupChange}
                    disabled={isSubscriptionExpired}
                    disableSorting
                    options={groupOptions}
                />
            </Tooltip>
        </MinimalTableCell>
    );
};

type ActionCellProps = {
    license: License;
    isLoading: boolean;
    onDelete: (license: License) => void;
};
export const ActionCell = (props: ActionCellProps) => (
    <MinimalTableCell align="right">
        <DangerOutlinedButton onClick={() => props.onDelete(props.license)} loading={props.isLoading}>
            <i className="fa-solid fa-trash" />
        </DangerOutlinedButton>
    </MinimalTableCell>
);
